import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Heart, ArrowLeft, Search, X, ChevronDown,
  Clock, CheckCircle, Send, Archive, Eye, Edit3,
  Calendar, AlertCircle, FileText, Mail, DollarSign, 
  RefreshCw, History, FileCheck, Copy, RotateCcw, Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

// Use relative URLs for same-origin requests (works in both preview and production)
// DO NOT use REACT_APP_BACKEND_URL - it can bake preview hosts into production builds
const INSIGHT_API = '/api/admin';

// Debug: Log that we're using relative URLs
console.log('[AdminWeddingInsight] Using relative API paths (same-origin)');
console.log('[AdminWeddingInsight] Current origin:', window.location.origin);

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', icon: Edit3 },
  sent: { label: 'Sent', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: Send },
  closed: { label: 'Closed', color: 'bg-stone-500/20 text-stone-400 border-stone-500/30', icon: Archive }
};

const AdminWeddingInsight = ({ user }) => {
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [fetchError, setFetchError] = useState(null); // For debug display
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Detail drawer
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Report generation (legacy deterministic)
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [editingReport, setEditingReport] = useState(false);
  const [editedReportText, setEditedReportText] = useState('');
  const [reportVersion, setReportVersion] = useState('detailed');
  
  // Grace Draft (new AI-enhanced system)
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [graceDraft, setGraceDraft] = useState(null);
  const [showDraftPreview, setShowDraftPreview] = useState(false);
  const [editingDraft, setEditingDraft] = useState(false);
  const [editedDraftText, setEditedDraftText] = useState('');
  const [clarificationFlags, setClarificationFlags] = useState([]);
  
  // Send confirmation modal
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [isResend, setIsResend] = useState(false);

  // Get admin ID from prop OR localStorage (same mechanism as main admin dashboard)
  const getAdminId = useCallback(() => {
    // First try from prop
    if (user?.id) return user.id;
    
    // Fallback: read from localStorage (same key used by App.js)
    try {
      const savedUser = localStorage.getItem('vowflow_user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        if (parsed?.id) return parsed.id;
      }
    } catch (e) {
      console.error('Failed to parse saved user:', e);
    }
    
    return null;
  }, [user?.id]);

  const fetchPurchases = useCallback(async (showRefresh = false) => {
    const adminId = getAdminId();
    
    // Guard: ensure admin ID is available
    if (!adminId) {
      console.error('Admin ID not available');
      setFetchError({ status: 'AUTH', message: 'Admin ID not available. Please log in again.' });
      setLoading(false);
      return;
    }
    
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setFetchError(null);
    
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('insight_status', statusFilter);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '100');
      
      const requestUrl = `${INSIGHT_API}/wedding-insight-purchases?${params.toString()}`;
      const fullUrl = `${window.location.origin}${requestUrl}`;
      console.log('[AdminWeddingInsight] Fetching purchases from:', fullUrl);
      console.log('[AdminWeddingInsight] With admin-id header:', adminId);
      
      const response = await axios.get(
        requestUrl,
        { headers: { 'admin-id': adminId } }
      );
      
      console.log('[AdminWeddingInsight] Response:', response.status, response.data?.purchases?.length, 'purchases');
      
      const filtered = (response.data.purchases || []).filter(p => p.intake_submitted === true);
      setPurchases(filtered);
      setTotalCount(filtered.length);
      setFetchError(null);
    } catch (error) {
      console.error('[AdminWeddingInsight] Error fetching purchases:', error);
      const requestedPath = `${INSIGHT_API}/wedding-insight-purchases`;
      const fullUrl = `${window.location.origin}${requestedPath}`;
      console.error('[AdminWeddingInsight] Request URL was:', fullUrl);
      console.error('[AdminWeddingInsight] Error response:', error.response?.status, error.response?.data);
      
      const status = error.response?.status || 'NETWORK';
      const message = error.response?.data?.detail || error.message || 'Unknown error';
      setFetchError({ status, message: JSON.stringify(message), url: fullUrl, path: requestedPath });
      toast.error('Failed to load Wedding Insight purchases');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAdminId, statusFilter, searchQuery]);

  useEffect(() => {
    // Wait for admin ID to be available, then fetch
    const adminId = getAdminId();
    if (adminId) {
      fetchPurchases();
    } else {
      // If no admin ID after a short delay, show error
      const timer = setTimeout(() => {
        if (!getAdminId()) {
          setFetchError({ status: 'AUTH', message: 'Not authenticated. Please log in as admin.' });
          setLoading(false);
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [fetchPurchases, getAdminId]);

  const fetchPurchaseDetails = async (purchase) => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    setSelectedPurchase(purchase);
    setNotesValue(purchase.insight_delivery_notes || '');
    setDrawerOpen(true);
    setShowReportPreview(false);
    setEditingReport(false);
    setGeneratedReport(purchase.insight_report || null);
    
    // Load Grace draft if available
    setShowDraftPreview(false);
    setEditingDraft(false);
    setGraceDraft(purchase.insight_draft || null);
    setClarificationFlags(purchase.insight_clarification_flags || []);
    
    if (purchase.insight_draft?.raw_email_text) {
      setEditedDraftText(purchase.insight_draft.raw_email_text || '');
    }
    
    if (purchase.insight_report?.raw_text) {
      setEditedReportText(purchase.insight_report.final_text || purchase.insight_report.raw_text.detailed || '');
    }
    
    try {
      const [auditRes, emailRes] = await Promise.all([
        axios.get(`${INSIGHT_API}/wedding-insight-admin-logs?purchase_id=${purchase.id}`, {
          headers: { 'admin-id': adminId }
        }),
        axios.get(`${INSIGHT_API}/wedding-insight-email-logs?limit=50`, {
          headers: { 'admin-id': adminId }
        })
      ]);
      
      setAuditLogs(auditRes.data.logs || []);
      const relevantEmails = (emailRes.data.logs || []).filter(
        log => log.purchase_id === purchase.id
      );
      setEmailLogs(relevantEmails);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const updateStatus = async (purchaseId, newStatus, notes = null) => {
    const adminId = getAdminId();
    if (!adminId) return;
    
    setUpdatingStatus(true);
    try {
      const body = { insight_status: newStatus };
      if (notes !== null) body.insight_delivery_notes = notes;
      
      await axios.post(
        `${INSIGHT_API}/wedding-insight-purchases/${purchaseId}/status`,
        body,
        { headers: { 'admin-id': adminId, 'Content-Type': 'application/json' } }
      );
      
      toast.success(`Status updated to ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchPurchases(true);
      
      if (selectedPurchase?.id === purchaseId) {
        setSelectedPurchase(prev => ({ ...prev, insight_status: newStatus }));
        const auditRes = await axios.get(
          `${INSIGHT_API}/wedding-insight-admin-logs?purchase_id=${purchaseId}`,
          { headers: { 'admin-id': adminId } }
        );
        setAuditLogs(auditRes.data.logs || []);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const generateReport = async () => {
    const adminId = getAdminId();
    if (!selectedPurchase || !adminId) return;
    
    setGeneratingReport(true);
    try {
      const response = await axios.post(
        `${INSIGHT_API}/wedding-insight-purchases/${selectedPurchase.id}/generate-report`,
        { version: reportVersion },
        { headers: { 'admin-id': adminId, 'Content-Type': 'application/json' } }
      );
      
      const report = response.data.report;
      setGeneratedReport(report);
      setEditedReportText(report.raw_text?.detailed || '');
      setShowReportPreview(true);
      
      // Update the selected purchase
      setSelectedPurchase(prev => ({ 
        ...prev, 
        insight_report: report,
        insight_report_generated_at: report.generated_at
      }));
      
      toast.success('Report generated successfully');
      
      // Refresh audit logs
      const auditRes = await axios.get(
        `${INSIGHT_API}/wedding-insight-admin-logs?purchase_id=${selectedPurchase.id}`,
        { headers: { 'admin-id': adminId } }
      );
      setAuditLogs(auditRes.data.logs || []);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  // NEW: Generate Grace Draft (AI-enhanced)
  const generateGraceDraft = async () => {
    const adminId = getAdminId();
    if (!selectedPurchase || !adminId) return;
    
    setGeneratingDraft(true);
    try {
      const response = await axios.post(
        `${INSIGHT_API}/wedding-insight-purchases/${selectedPurchase.id}/generate-draft`,
        {},
        { headers: { 'admin-id': adminId, 'Content-Type': 'application/json' } }
      );
      
      // Handle needs_clarification status
      if (response.data.status === 'needs_clarification') {
        setClarificationFlags(response.data.clarification_flags || []);
        toast.error('Draft needs clarification - missing required data');
        return;
      }
      
      const draft = response.data.draft;
      setGraceDraft(draft);
      setEditedDraftText(draft?.raw_email_text || '');
      setClarificationFlags(response.data.clarification_flags || []);
      setShowDraftPreview(true);
      
      // Update the selected purchase
      setSelectedPurchase(prev => ({ 
        ...prev, 
        insight_draft: draft,
        insight_draft_generated_at: response.data.generated_at,
        insight_engine_version: response.data.engine_version,
        insight_template_version: response.data.template_version
      }));
      
      toast.success('Grace draft generated successfully');
      
      // Refresh audit logs
      const auditRes = await axios.get(
        `${INSIGHT_API}/wedding-insight-admin-logs?purchase_id=${selectedPurchase.id}`,
        { headers: { 'admin-id': adminId } }
      );
      setAuditLogs(auditRes.data.logs || []);
    } catch (error) {
      console.error('Error generating Grace draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate Grace draft');
    } finally {
      setGeneratingDraft(false);
    }
  };

  const saveEditedReport = async () => {
    const adminId = getAdminId();
    if (!selectedPurchase || !adminId) return;
    
    try {
      // Store the edited text in the purchase document
      await axios.post(
        `${INSIGHT_API}/wedding-insight-purchases/${selectedPurchase.id}/status`,
        { 
          insight_status: selectedPurchase.insight_status || 'in_progress',
          insight_delivery_notes: notesValue
        },
        { headers: { 'admin-id': adminId, 'Content-Type': 'application/json' } }
      );
      
      // Update local state
      setGeneratedReport(prev => ({
        ...prev,
        final_text: editedReportText
      }));
      
      setEditingReport(false);
      toast.success('Edits saved');
    } catch (error) {
      console.error('Error saving edits:', error);
      toast.error('Failed to save edits');
    }
  };

  const openSendModal = (resend = false) => {
    setIsResend(resend);
    setShowSendModal(true);
  };

  const sendReport = async () => {
    const adminId = getAdminId();
    if (!selectedPurchase || !generatedReport || !adminId) return;
    
    setSendingReport(true);
    try {
      // Build subject line: "Your Wedding Insight — {FirstName} — {WeddingDate}"
      const firstName = generatedReport.customer?.first_name || selectedPurchase.name?.split(' ')[0] || 'there';
      const weddingDate = generatedReport.wedding?.date_short || '';
      const subjectLine = weddingDate 
        ? `Your Wedding Insight — ${firstName} — ${weddingDate}`
        : `Your Wedding Insight — ${firstName}`;
      
      const response = await axios.post(
        `${INSIGHT_API}/wedding-insight-purchases/${selectedPurchase.id}/send-report`,
        { 
          version: reportVersion,
          custom_note: null,
          subject_override: subjectLine,
          final_text: editedReportText || null,
          is_resend: isResend
        },
        { headers: { 'admin-id': adminId, 'Content-Type': 'application/json' } }
      );
      
      toast.success(`Report ${isResend ? 'resent' : 'sent'} to ${response.data.sent_to}`);
      setShowSendModal(false);
      
      // Update local state
      setSelectedPurchase(prev => ({
        ...prev,
        insight_status: 'sent',
        insight_sent_at: new Date().toISOString(),
        insight_sent_to_email: response.data.sent_to
      }));
      
      // Refresh data
      fetchPurchases(true);
      
      // Refresh logs
      const [auditRes, emailRes] = await Promise.all([
        axios.get(`${INSIGHT_API}/wedding-insight-admin-logs?purchase_id=${selectedPurchase.id}`, {
          headers: { 'admin-id': adminId }
        }),
        axios.get(`${INSIGHT_API}/wedding-insight-email-logs?limit=50`, {
          headers: { 'admin-id': adminId }
        })
      ]);
      setAuditLogs(auditRes.data.logs || []);
      setEmailLogs((emailRes.data.logs || []).filter(log => log.purchase_id === selectedPurchase.id));
      
    } catch (error) {
      console.error('Error sending report:', error);
      toast.error(error.response?.data?.detail || 'Failed to send report. Status NOT updated.');
    } finally {
      setSendingReport(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const saveNotes = async () => {
    const adminId = getAdminId();
    if (!selectedPurchase || !adminId) return;
    
    try {
      await axios.post(
        `${INSIGHT_API}/wedding-insight-purchases/${selectedPurchase.id}/status`,
        { 
          insight_status: selectedPurchase.insight_status || 'pending',
          insight_delivery_notes: notesValue 
        },
        { headers: { 'admin-id': adminId, 'Content-Type': 'application/json' } }
      );
      
      toast.success('Notes saved');
      setEditingNotes(false);
      setSelectedPurchase(prev => ({ ...prev, insight_delivery_notes: notesValue }));
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Check admin access
  if (!user?.is_admin && user?.role !== 'admin' && user?.role !== 'super_admin') {
    return (
      <div className="min-h-screen bg-[#1C1917] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl text-white mb-2">Access Denied</h1>
          <p className="text-white/60 mb-4">You must be an admin to view this page.</p>
          <Button onClick={() => navigate('/admin/login')} className="bg-[#881337]">
            Admin Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1917]">
      {/* Header */}
      <header className="bg-[#292524] border-b border-white/10 px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div className="flex items-center gap-2">
              <Heart className="text-[#881337]" size={24} fill="#881337" />
              <h1 className="text-xl font-serif text-white">Wedding Insight Inbox</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/40 text-sm hidden md:block">
              {totalCount} submission{totalCount !== 1 ? 's' : ''}
            </span>
            <Button
              onClick={() => fetchPurchases(true)}
              variant="outline"
              size="sm"
              className="border-white/20 text-white/70 hover:bg-white/10"
              disabled={refreshing}
            >
              <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </header>

      {/* Filters Bar */}
      <div className="bg-[#292524] border-b border-white/10 px-4 md:px-6 py-3">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={18} />
            <Input
              type="text"
              placeholder="Search by name, email, or purchase ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>
          
          <div className="relative">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="appearance-none bg-white/5 border border-white/10 text-white rounded-md px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#881337]/50"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="sent">Sent</option>
              <option value="closed">Closed</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" size={16} />
          </div>
          
          {(statusFilter || searchQuery) && (
            <Button
              onClick={() => { setStatusFilter(''); setSearchQuery(''); }}
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white"
            >
              <X size={16} className="mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-pulse flex flex-col items-center gap-4">
              <Heart className="w-10 h-10 text-[#881337]" />
              <p className="text-white/60">Loading...</p>
            </div>
          </div>
        ) : fetchError ? (
          <div className="text-center py-20">
            <AlertCircle className="w-16 h-16 text-red-400/60 mx-auto mb-4" />
            <h2 className="text-xl text-white/60 mb-2">Failed to load purchases</h2>
            <p className="text-white/40 mb-4">
              {fetchError.status === 'AUTH' 
                ? 'Please log in as admin to view Wedding Insight purchases.'
                : 'There was an error loading the data.'}
            </p>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 max-w-lg mx-auto text-left overflow-x-auto">
              <p className="text-red-400 text-sm font-mono break-all">
                <strong>Status:</strong> {fetchError.status}<br />
                <strong>Path:</strong> {fetchError.path || 'N/A'}<br />
                <strong>Full URL:</strong> {fetchError.url || 'N/A'}<br />
                <strong>Detail:</strong> {fetchError.message}
              </p>
            </div>
            <p className="text-white/30 text-xs mt-2">
              Origin: {window.location.origin}
            </p>
            <button
              onClick={() => fetchPurchases()}
              className="mt-4 px-4 py-2 bg-[#881337] text-white rounded-lg hover:bg-[#9F1239] transition"
            >
              Retry
            </button>
          </div>
        ) : purchases.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h2 className="text-xl text-white/60 mb-2">No submissions found</h2>
            <p className="text-white/40">
              {statusFilter || searchQuery 
                ? 'Try adjusting your filters' 
                : 'Wedding Insight intake submissions will appear here'}
            </p>
          </div>
        ) : (
          <div className="bg-[#292524] rounded-xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 bg-white/5 text-white/50 text-xs uppercase tracking-wider font-medium">
              <div className="col-span-2">Submitted</div>
              <div className="col-span-3">Customer</div>
              <div className="col-span-2">Wedding</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Actions</div>
            </div>
            
            <div className="divide-y divide-white/5">
              {purchases.map((purchase) => {
                const status = STATUS_CONFIG[purchase.insight_status] || STATUS_CONFIG.pending;
                const StatusIcon = status.icon;
                const intake = purchase.intake_data || {};
                const hasReport = !!purchase.insight_report;
                
                return (
                  <div 
                    key={purchase.id}
                    className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-4 hover:bg-white/5 transition-colors"
                  >
                    <div className="col-span-2 flex items-center gap-2">
                      <Calendar size={14} className="text-white/40 hidden md:block" />
                      <div>
                        <p className="text-white text-sm">{formatShortDate(purchase.intake_submitted_at)}</p>
                        <p className="text-white/40 text-xs">
                          {purchase.intake_submitted_at 
                            ? new Date(purchase.intake_submitted_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                            : ''}
                        </p>
                      </div>
                    </div>
                    
                    <div className="col-span-3">
                      <p className="text-white font-medium text-sm">{intake.name || purchase.name || '—'}</p>
                      <p className="text-white/50 text-xs truncate">{intake.email || purchase.email}</p>
                    </div>
                    
                    <div className="col-span-2 hidden md:block">
                      <p className="text-white/70 text-sm">{formatShortDate(intake.wedding_date)}</p>
                      <p className="text-white/40 text-xs truncate">{intake.venue?.split(',')[0] || '—'}</p>
                    </div>
                    
                    <div className="col-span-2">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${status.color}`}>
                          <StatusIcon size={12} />
                          {status.label}
                        </span>
                        {hasReport && (
                          <FileCheck size={14} className="text-green-400" title="Report generated" />
                        )}
                      </div>
                    </div>
                    
                    <div className="col-span-3 flex items-center gap-2 flex-wrap">
                      <Button
                        onClick={() => fetchPurchaseDetails(purchase)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white/70 hover:bg-white/10 text-xs px-2 py-1 h-7"
                      >
                        <Eye size={12} className="mr-1" /> View
                      </Button>
                      
                      {purchase.insight_status !== 'in_progress' && purchase.insight_status !== 'sent' && (
                        <Button
                          onClick={() => updateStatus(purchase.id, 'in_progress')}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1 h-7"
                          disabled={updatingStatus}
                        >
                          <Edit3 size={12} className="mr-1" /> Start
                        </Button>
                      )}
                      
                      {purchase.insight_status === 'sent' && (
                        <Button
                          onClick={() => updateStatus(purchase.id, 'closed')}
                          size="sm"
                          variant="outline"
                          className="border-white/20 text-white/50 hover:bg-white/10 text-xs px-2 py-1 h-7"
                          disabled={updatingStatus}
                        >
                          <Archive size={12} className="mr-1" /> Close
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Detail Drawer */}
      {drawerOpen && selectedPurchase && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          
          <div className="relative w-full max-w-3xl bg-[#1C1917] border-l border-white/10 overflow-y-auto">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-[#292524] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-lg font-serif text-white">
                  {selectedPurchase.intake_data?.name || selectedPurchase.name}
                </h2>
                <p className="text-white/50 text-sm">{selectedPurchase.intake_data?.email || selectedPurchase.email}</p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="text-white/60 hover:text-white p-2"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Drawer Content */}
            <div className="p-6 space-y-6">
              {/* Grace Draft Generation Section (NEW) */}
              <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Sparkles size={16} className="text-purple-400" />
                    Generate with Grace
                  </h3>
                  {graceDraft && selectedPurchase.insight_draft_generated_at && (
                    <span className="text-xs text-white/40">
                      Draft from {formatDate(selectedPurchase.insight_draft_generated_at)}
                    </span>
                  )}
                </div>
                
                <p className="text-white/50 text-sm mb-4">
                  Grace analyzes the intake data with deterministic logic, then phrases it warmly. Nothing auto-sends.
                </p>

                {/* Grace Draft Actions */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    onClick={generateGraceDraft}
                    size="sm"
                    className="bg-gradient-to-r from-purple-600 to-[#881337] hover:from-purple-700 hover:to-[#9F1239]"
                    disabled={generatingDraft}
                    data-testid="generate-grace-draft-btn"
                  >
                    {generatingDraft ? (
                      <>
                        <RefreshCw size={14} className="mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : graceDraft ? (
                      <>
                        <RefreshCw size={14} className="mr-1" />
                        Regenerate Draft
                      </>
                    ) : (
                      <>
                        <Sparkles size={14} className="mr-1" />
                        Generate Draft
                      </>
                    )}
                  </Button>

                  {graceDraft && (
                    <>
                      <Button
                        onClick={() => setShowDraftPreview(!showDraftPreview)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white/70 hover:bg-white/10"
                        data-testid="preview-grace-draft-btn"
                      >
                        <Eye size={14} className="mr-1" />
                        {showDraftPreview ? 'Hide Draft' : 'Preview Draft'}
                      </Button>
                    </>
                  )}
                </div>

                {/* Clarification Flags */}
                {clarificationFlags.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                    <p className="text-amber-400 text-sm font-medium flex items-center gap-2 mb-2">
                      <AlertCircle size={14} />
                      Needs Clarification
                    </p>
                    <ul className="text-amber-200/70 text-xs space-y-1">
                      {clarificationFlags.map((flag, idx) => (
                        <li key={idx}>• {flag.field}: {flag.message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Grace Draft Preview */}
                {showDraftPreview && graceDraft && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-white/50">Grace Draft Preview</span>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => copyToClipboard(editedDraftText || graceDraft.raw_email_text || '')}
                          size="sm"
                          variant="ghost"
                          className="text-white/50 hover:text-white h-7"
                        >
                          <Copy size={12} className="mr-1" /> Copy
                        </Button>
                        <Button
                          onClick={() => setEditingDraft(!editingDraft)}
                          size="sm"
                          variant="ghost"
                          className="text-white/50 hover:text-white h-7"
                        >
                          <Edit3 size={12} className="mr-1" /> {editingDraft ? 'Cancel' : 'Edit'}
                        </Button>
                      </div>
                    </div>

                    {editingDraft ? (
                      <div className="space-y-3">
                        <textarea
                          value={editedDraftText}
                          onChange={(e) => setEditedDraftText(e.target.value)}
                          className="w-full h-96 bg-white/5 border border-white/10 rounded-lg p-4 text-white text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                          data-testid="draft-edit-textarea"
                        />
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => {
                              setEditingDraft(false);
                              toast.success('Draft edits saved locally');
                            }}
                            size="sm" 
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            Save Edits
                          </Button>
                          <Button 
                            onClick={() => {
                              setEditingDraft(false);
                              setEditedDraftText(graceDraft.raw_email_text || '');
                            }}
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white/70"
                          >
                            Discard
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white/5 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="text-white/80 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                          {editedDraftText || graceDraft.raw_email_text || 'No content generated'}
                        </pre>
                      </div>
                    )}

                    {/* Deterministic Analysis Summary */}
                    {graceDraft.sections && (
                      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                        <div className="bg-white/5 rounded p-2">
                          <p className="text-white/40">Working Items</p>
                          <p className="text-white font-medium">{graceDraft.sections.whats_working?.length || 0}</p>
                        </div>
                        <div className="bg-white/5 rounded p-2">
                          <p className="text-white/40">Risk Flags</p>
                          <p className="text-white font-medium">{graceDraft.sections.risk_flags?.length || 0}</p>
                        </div>
                        <div className="bg-white/5 rounded p-2">
                          <p className="text-white/40">Action Items</p>
                          <p className="text-white font-medium">{graceDraft.sections.action_plan?.length || 0}</p>
                        </div>
                      </div>
                    )}

                    {/* Version info */}
                    {selectedPurchase.insight_engine_version && (
                      <div className="mt-3 text-xs text-white/30">
                        Engine v{selectedPurchase.insight_engine_version} • Template v{selectedPurchase.insight_template_version}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Legacy Report Generation Section (kept for backwards compatibility) */}
              <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <FileCheck size={16} className="text-[#881337]" />
                    Send Report
                  </h3>
                  {generatedReport?.generated_at && (
                    <span className="text-xs text-white/40">
                      Generated {formatDate(generatedReport.generated_at)}
                    </span>
                  )}
                </div>

                {/* Report Actions */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    onClick={generateReport}
                    size="sm"
                    className="bg-[#881337] hover:bg-[#9F1239]"
                    disabled={generatingReport}
                    data-testid="generate-report-btn"
                  >
                    {generatingReport ? (
                      <>
                        <RefreshCw size={14} className="mr-1 animate-spin" />
                        Generating...
                      </>
                    ) : generatedReport ? (
                      <>
                        <RefreshCw size={14} className="mr-1" />
                        Regenerate
                      </>
                    ) : (
                      <>
                        <FileText size={14} className="mr-1" />
                        Generate (Legacy)
                      </>
                    )}
                  </Button>

                  {generatedReport && (
                    <>
                      <Button
                        onClick={() => setShowReportPreview(!showReportPreview)}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white/70 hover:bg-white/10"
                      >
                        <Eye size={14} className="mr-1" />
                        {showReportPreview ? 'Hide' : 'Preview'}
                      </Button>
                      
                      {selectedPurchase.insight_status === 'sent' ? (
                        <Button
                          onClick={() => openSendModal(true)}
                          size="sm"
                          variant="outline"
                          className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                          data-testid="resend-report-btn"
                        >
                          <RotateCcw size={14} className="mr-1" />
                          Resend
                        </Button>
                      ) : (
                        <Button
                          onClick={() => openSendModal(false)}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          data-testid="send-report-btn"
                        >
                          <Send size={14} className="mr-1" />
                          Send Report
                        </Button>
                      )}
                    </>
                  )}
                </div>

                {/* Version selector */}
                {!generatedReport && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white/50">Version:</span>
                    <select
                      value={reportVersion}
                      onChange={(e) => setReportVersion(e.target.value)}
                      className="bg-white/5 border border-white/10 text-white rounded px-2 py-1 text-sm"
                    >
                      <option value="detailed">Detailed</option>
                      <option value="short">Short</option>
                    </select>
                  </div>
                )}

                {/* Report Preview */}
                {showReportPreview && generatedReport && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-white/50">Report Preview</span>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => copyToClipboard(editedReportText || generatedReport.raw_text?.detailed || '')}
                          size="sm"
                          variant="ghost"
                          className="text-white/50 hover:text-white h-7"
                        >
                          <Copy size={12} className="mr-1" /> Copy
                        </Button>
                        <Button
                          onClick={() => setEditingReport(!editingReport)}
                          size="sm"
                          variant="ghost"
                          className="text-white/50 hover:text-white h-7"
                        >
                          <Edit3 size={12} className="mr-1" /> {editingReport ? 'Cancel' : 'Edit'}
                        </Button>
                      </div>
                    </div>

                    {editingReport ? (
                      <div className="space-y-3">
                        <textarea
                          value={editedReportText}
                          onChange={(e) => setEditedReportText(e.target.value)}
                          className="w-full h-96 bg-white/5 border border-white/10 rounded-lg p-4 text-white text-sm font-mono leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#881337]/50 resize-none"
                        />
                        <div className="flex gap-2">
                          <Button onClick={saveEditedReport} size="sm" className="bg-[#881337]">
                            Save Edits
                          </Button>
                          <Button 
                            onClick={() => {
                              setEditingReport(false);
                              setEditedReportText(generatedReport.raw_text?.detailed || '');
                            }}
                            size="sm"
                            variant="outline"
                            className="border-white/20 text-white/70"
                          >
                            Discard
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white/5 rounded-lg p-4 max-h-96 overflow-y-auto">
                        <pre className="text-white/80 text-sm whitespace-pre-wrap font-sans leading-relaxed">
                          {editedReportText || generatedReport.raw_text?.detailed || 'No content generated'}
                        </pre>
                      </div>
                    )}

                    {/* Quick Stats */}
                    {generatedReport.sections && (
                      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
                        <div className="bg-white/5 rounded p-2">
                          <p className="text-white/40">Working Items</p>
                          <p className="text-white font-medium">{generatedReport.sections.whats_working?.length || 0}</p>
                        </div>
                        <div className="bg-white/5 rounded p-2">
                          <p className="text-white/40">Risk Flags</p>
                          <p className="text-white font-medium">{generatedReport.sections.risk_flags?.length || 0}</p>
                        </div>
                        <div className="bg-white/5 rounded p-2">
                          <p className="text-white/40">Vendor Gaps</p>
                          <p className="text-white font-medium">{generatedReport.sections.vendor_gaps?.length || 0}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Status & Quick Actions */}
              <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/50 text-sm">Current Status</span>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border ${STATUS_CONFIG[selectedPurchase.insight_status]?.color || STATUS_CONFIG.pending.color}`}>
                    {STATUS_CONFIG[selectedPurchase.insight_status]?.label || 'Pending'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {['pending', 'in_progress', 'sent', 'closed'].map((status) => {
                    const config = STATUS_CONFIG[status];
                    const Icon = config.icon;
                    const isActive = selectedPurchase.insight_status === status;
                    return (
                      <Button
                        key={status}
                        onClick={() => updateStatus(selectedPurchase.id, status)}
                        size="sm"
                        variant={isActive ? 'default' : 'outline'}
                        className={isActive 
                          ? status === 'pending' ? 'bg-amber-600' 
                            : status === 'in_progress' ? 'bg-blue-600'
                            : status === 'sent' ? 'bg-green-600'
                            : 'bg-stone-600'
                          : 'border-white/20 text-white/70'}
                        disabled={updatingStatus}
                      >
                        <Icon size={14} className="mr-1" /> {config.label}
                      </Button>
                    );
                  })}
                </div>
                
                {selectedPurchase.insight_status === 'sent' && selectedPurchase.insight_sent_at && (
                  <div className="mt-4 pt-4 border-t border-white/10 text-sm">
                    <p className="text-white/50">
                      Sent to <span className="text-white">{selectedPurchase.insight_sent_to_email}</span>
                    </p>
                    <p className="text-white/50">
                      on <span className="text-white">{formatDate(selectedPurchase.insight_sent_at)}</span>
                    </p>
                    <p className="text-white/50">
                      by <span className="text-white">{selectedPurchase.insight_sent_by}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Purchase Info */}
              <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <DollarSign size={16} className="text-[#881337]" />
                  Purchase Details
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-white/50">Purchase ID</p>
                    <p className="text-white font-mono text-xs break-all">{selectedPurchase.id}</p>
                  </div>
                  <div>
                    <p className="text-white/50">Created</p>
                    <p className="text-white">{formatDate(selectedPurchase.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-white/50">Intake Submitted</p>
                    <p className="text-white">{formatDate(selectedPurchase.intake_submitted_at)}</p>
                  </div>
                  <div>
                    <p className="text-white/50">Amount</p>
                    <p className="text-white">${selectedPurchase.amount_paid || '99'}</p>
                  </div>
                </div>
              </div>

              {/* Intake Data */}
              {selectedPurchase.intake_data && (
                <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <FileText size={16} className="text-[#881337]" />
                    Intake Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <p className="text-white/50">Wedding Date</p>
                        <p className="text-white">{selectedPurchase.intake_data.wedding_date || '—'}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Guest Count</p>
                        <p className="text-white">{selectedPurchase.intake_data.guest_count || '—'}</p>
                      </div>
                      <div className="col-span-2">
                        <p className="text-white/50">Venue</p>
                        <p className="text-white">{selectedPurchase.intake_data.venue || '—'}</p>
                      </div>
                      <div>
                        <p className="text-white/50">Planning Stage</p>
                        <p className="text-white">{selectedPurchase.intake_data.planning_stage || '—'}</p>
                      </div>
                      <div>
                        <p className="text-white/50">First Look</p>
                        <p className="text-white">{selectedPurchase.intake_data.first_look || '—'}</p>
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-white/50 text-sm mb-2">Vendors Booked</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(selectedPurchase.intake_data.vendors_booked || []).length > 0 ? (
                          selectedPurchase.intake_data.vendors_booked.map((vendor, i) => (
                            <span key={i} className="px-2 py-1 bg-white/10 rounded text-white text-xs">
                              {vendor}
                            </span>
                          ))
                        ) : (
                          <span className="text-white/40 text-sm">None specified</span>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <p className="text-white/50 text-sm mb-2">Biggest Concern</p>
                      <p className="text-white text-sm bg-white/5 rounded-lg p-3 whitespace-pre-wrap">
                        {selectedPurchase.intake_data.biggest_concern || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Internal Notes */}
              <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium flex items-center gap-2">
                    <Edit3 size={16} className="text-[#881337]" />
                    Internal Notes
                  </h3>
                  {!editingNotes && (
                    <Button
                      onClick={() => setEditingNotes(true)}
                      size="sm"
                      variant="ghost"
                      className="text-white/60 hover:text-white"
                    >
                      Edit
                    </Button>
                  )}
                </div>
                
                {editingNotes ? (
                  <div className="space-y-3">
                    <textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      placeholder="Add internal notes..."
                      className="w-full h-32 bg-white/5 border border-white/10 rounded-lg p-3 text-white text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-[#881337]/50 resize-none"
                    />
                    <div className="flex gap-2">
                      <Button onClick={saveNotes} size="sm" className="bg-[#881337]">
                        Save Notes
                      </Button>
                      <Button 
                        onClick={() => { setEditingNotes(false); setNotesValue(selectedPurchase.insight_delivery_notes || ''); }}
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white/70"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-white/70 text-sm whitespace-pre-wrap">
                    {selectedPurchase.insight_delivery_notes || 'No notes yet.'}
                  </p>
                )}
              </div>

              {/* Audit History */}
              <div className="bg-[#292524] rounded-xl p-4 border border-white/10">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <History size={16} className="text-[#881337]" />
                  Activity History
                </h3>
                
                {auditLogs.length === 0 && emailLogs.length === 0 ? (
                  <p className="text-white/40 text-sm">No activity recorded yet.</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {[
                      ...auditLogs.map(log => ({ ...log, type: 'audit' })),
                      ...emailLogs.map(log => ({ ...log, type: 'email', changed_at: log.sent_at }))
                    ]
                      .sort((a, b) => new Date(b.changed_at || b.sent_at) - new Date(a.changed_at || a.sent_at))
                      .map((log, i) => (
                        <div key={i} className="flex gap-3 text-sm">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            log.type === 'email' ? 'bg-blue-500/20' : 
                            log.action === 'report_generated' ? 'bg-purple-500/20' :
                            log.action === 'report_sent' ? 'bg-green-500/20' :
                            'bg-[#881337]/20'
                          }`}>
                            {log.type === 'email' ? (
                              <Mail size={14} className="text-blue-400" />
                            ) : log.action === 'report_generated' ? (
                              <FileText size={14} className="text-purple-400" />
                            ) : log.action === 'report_sent' ? (
                              <Send size={14} className="text-green-400" />
                            ) : (
                              <RefreshCw size={14} className="text-[#881337]" />
                            )}
                          </div>
                          <div className="flex-1">
                            {log.type === 'email' ? (
                              <>
                                <p className="text-white">
                                  {log.email_type === 'insight_report_delivery' ? 'Report delivered' : 'Email'} 
                                  <span className={log.status === 'sent' ? 'text-green-400' : 'text-red-400'}> {log.status}</span>
                                </p>
                                <p className="text-white/40 text-xs">
                                  To: {log.sent_to} • {formatDate(log.sent_at)}
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-white">
                                  {log.action === 'report_generated' ? 'Report generated' :
                                   log.action === 'report_sent' ? 'Report sent' :
                                   <>Status: <span className="text-white/60">{log.old_status}</span> → <span className="text-[#881337]">{log.new_status}</span></>}
                                </p>
                                <p className="text-white/40 text-xs">
                                  By {log.changed_by} • {formatDate(log.changed_at)}
                                </p>
                                {log.notes && (
                                  <p className="text-white/50 text-xs mt-1 italic">&quot;{log.notes}&quot;</p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Confirmation Modal */}
      {showSendModal && selectedPurchase && generatedReport && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowSendModal(false)}
          />
          
          <div className="relative bg-[#292524] rounded-2xl border border-white/10 w-full max-w-lg overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-lg font-serif text-white flex items-center gap-2">
                {isResend ? (
                  <>
                    <RotateCcw size={20} className="text-amber-400" />
                    Confirm Resend
                  </>
                ) : (
                  <>
                    <Send size={20} className="text-green-400" />
                    Confirm Send
                  </>
                )}
              </h2>
            </div>
            
            <div className="p-6 space-y-4">
              {isResend && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-400 text-sm">
                  This report has already been sent. Are you sure you want to send it again?
                </div>
              )}
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Recipient</span>
                  <span className="text-white font-medium">{generatedReport.customer?.name || selectedPurchase.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Email</span>
                  <span className="text-white">{generatedReport.customer?.email || selectedPurchase.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Purchase ID</span>
                  <span className="text-white font-mono text-xs">{selectedPurchase.id.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Subject</span>
                  <span className="text-white text-xs">
                    Your Wedding Insight — {generatedReport.customer?.first_name} — {generatedReport.wedding?.date_short || ''}
                  </span>
                </div>
              </div>
              
              <div className="border-t border-white/10 pt-4">
                <p className="text-white/50 text-xs mb-2">Report Preview</p>
                <div className="bg-white/5 rounded-lg p-3 max-h-32 overflow-y-auto">
                  <p className="text-white/70 text-xs whitespace-pre-wrap">
                    {(editedReportText || generatedReport.raw_text?.short || '').slice(0, 400)}...
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <Button
                onClick={() => setShowSendModal(false)}
                variant="outline"
                className="border-white/20 text-white/70"
              >
                Cancel
              </Button>
              <Button
                onClick={sendReport}
                className={isResend ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
                disabled={sendingReport}
              >
                {sendingReport ? (
                  <>
                    <RefreshCw size={14} className="mr-1 animate-spin" />
                    Sending...
                  </>
                ) : isResend ? (
                  <>
                    <RotateCcw size={14} className="mr-1" />
                    Resend Report
                  </>
                ) : (
                  <>
                    <Send size={14} className="mr-1" />
                    Send Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWeddingInsight;
